import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { resolveTestCaseValue } from '../utils/dataTransform';

interface TagDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  tagName: string;
  tagType: string;
  allResults: any[];
  onNavigateToCase: (resultId: string, stepIndex: number) => void;
}

const Modal = styled.div<{ isOpen: boolean }>`
  display: ${props => props.isOpen ? 'block' : 'none'};
  position: fixed;
  z-index: 10001;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
`;

const ModalContent = styled.div`
  background-color: #fefefe;
  margin: 3% auto;
  padding: 20px;
  border: 1px solid #888;
  border-radius: 8px;
  width: 95%;
  max-width: 1200px;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  border-bottom: 2px solid #f0f0f0;
  padding-bottom: 10px;
`;

const ModalTitle = styled.h2`
  margin: 0;
  color: #333;
`;

const TagBadge = styled.span`
  background: #e3f2fd;
  color: #1976d2;
  padding: 4px 12px;
  border-radius: 16px;
  font-size: 14px;
  font-weight: 500;
  margin-left: 10px;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 28px;
  cursor: pointer;
  color: #aaa;
  
  &:hover {
    color: #000;
  }
`;

const SummarySection = styled.div`
  background: #f8f9fa;
  padding: 15px;
  border-radius: 8px;
  margin-bottom: 20px;
  display: flex;
  gap: 30px;
`;

const SummaryItem = styled.div`
  display: flex;
  flex-direction: column;
`;

const SummaryLabel = styled.span`
  font-size: 12px;
  color: #666;
  margin-bottom: 4px;
`;

const SummaryValue = styled.span<{ isNg?: boolean }>`
  font-size: 20px;
  font-weight: bold;
  color: ${props => props.isNg ? '#f44336' : '#333'};
`;

const CaseTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 10px;
`;

const TableHeader = styled.thead`
  background: #f5f5f5;
`;

const TableRow = styled.tr<{ isClickable?: boolean }>`
  border-bottom: 1px solid #ddd;
  cursor: ${props => props.isClickable ? 'pointer' : 'default'};
  
  &:hover {
    background: ${props => props.isClickable ? '#f0f7ff' : '#f9f9f9'};
  }
`;

const TableHeaderCell = styled.th`
  padding: 12px;
  text-align: left;
  font-weight: 600;
  color: #555;
`;

const TableCell = styled.td`
  padding: 10px 12px;
  vertical-align: top;
`;

const CaseIdLink = styled.span`
  color: #1976d2;
  text-decoration: underline;
  cursor: pointer;
  
  &:hover {
    color: #1565c0;
  }
`;

const StepInfo = styled.div`
  font-size: 14px;
  color: #666;
`;

const JudgmentBadge = styled.span<{ match: boolean }>`
  padding: 3px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  color: white;
  background: ${props => props.match ? '#4caf50' : '#f44336'};
`;

const StepDescription = styled.div`
  font-size: 13px;
  color: #555;
  margin-top: 4px;
  max-width: 400px;
  line-height: 1.4;
`;

const NoDataMessage = styled.div`
  text-align: center;
  padding: 40px;
  color: #999;
  font-size: 16px;
`;

const TagDetailModal: React.FC<TagDetailModalProps> = ({
  isOpen,
  onClose,
  tagName,
  tagType,
  allResults,
  onNavigateToCase
}) => {
  const [tagDetails, setTagDetails] = useState<any[]>([]);
  const [summary, setSummary] = useState({ total: 0, ngCount: 0, okCount: 0, confirmedCount: 0 });

  // タグ統計のキーは tagAnalysisService が `action_tags:<tag>` 形式で生成するため、
  // TagInfoButton からはプレフィックス付きの名前が渡ってくる。一方 evaluated_tags の
  // action_tags は生のタグ名なので、突合・表示の前にプレフィックスを外す。
  const normalizedTagName = tagName?.replace(/^action_tags:/, '') ?? tagName;

  useEffect(() => {
    if (!isOpen || !tagName) return;

    const details: any[] = [];
    let totalCount = 0;
    let ngCount = 0;
    let okCount = 0;
    let confirmedCount = 0;  // 確認済みの数
    let confirmedNgCount = 0;  // 確認済みのNG数

    // 各テスト結果を処理
    allResults.forEach(result => {
      let value = result.value;
      if (typeof value === 'string') {
        try {
          value = JSON.parse(value);
        } catch (e) {
          return;
        }
      }

      // ステップ定義の復元元を解決する。
      // 結果 value 内の testcase_snapshot を優先し、無ければ live のテストケース value へ
      // フォールバックする（凍結廃止後も snapshot から復元されるようにする / #886）。
      // タグ一致ステップがある場合のみ評価し、warn は結果あたり高々1回に抑える。
      let resolvedTestCaseValue: any;
      const getTestCaseValue = () => {
        if (resolvedTestCaseValue === undefined) {
          resolvedTestCaseValue = resolveTestCaseValue(
            value,
            () => result.test_case?.value ?? {},
            'TagDetailModal'
          );
        }
        return resolvedTestCaseValue;
      };

      // タグ分析データを取得
      const tagAnalysis = value?.tag_analysis || {};
      const steps = tagAnalysis.steps || [];
      const isConfirmed = tagAnalysis.human_judgment_confirmed === true;

      // 各ステップを確認
      steps.forEach((step: any, stepIndex: number) => {
        const stepTags = step.evaluated_tags || {};
        let hasTag = false;

        // タグタイプに応じてチェック
        if (tagType === 'action') {
          const actionTags = stepTags.action_tags || [];
          hasTag = actionTags.includes(normalizedTagName);
        } else {
          const tagList = stepTags[tagType] || [];
          const normalizedTagList = typeof tagList === 'string'
            ? tagList.split(',').map((t: string) => t.trim())
            : tagList;
          hasTag = normalizedTagList.includes(normalizedTagName);
        }

        if (hasTag) {
          // 判定結果を確認
          const isNg = step.judgment_match === false || step.ai_interpretation_correct === false;
          
          totalCount++;
          if (isNg) {
            ngCount++;
          } else {
            okCount++;
          }
          
          // 確認済みの場合のみ、NG率計算用にカウント
          if (isConfirmed) {
            confirmedCount++;
            if (isNg) {
              confirmedNgCount++;
            }
          }

          // テストケース情報を取得
          const testCase = result.test_case || {};
          
          // ステップの説明を取得
          let stepDescription = '';
          const testCaseValue = getTestCaseValue();
          if (testCaseValue?.teststep && testCaseValue.teststep[stepIndex]) {
            const testStep = testCaseValue.teststep[stepIndex];
            stepDescription = testStep.expect || testStep.description || '';
          }

          details.push({
            resultId: result.id,
            caseId: result.test_case_id,
            caseName: testCase.name || testCase.value?.name || `Case ${result.test_case_id}`,  // primary_item等は廃止
            stepIndex: stepIndex + 1,
            stepDescription,
            humanJudgment: step.human_judgment || '未評価',
            aiJudgment: step.ai_judgment || '未評価',
            judgmentMatch: step.judgment_match,
            aiInterpretationCorrect: step.ai_interpretation_correct,
            isNg,
            isConfirmed
          });
        }
      });
    });

    // NG率でソート（NGのものを上に）
    details.sort((a, b) => {
      if (a.isNg !== b.isNg) return b.isNg ? 1 : -1;
      return 0;
    });

    setTagDetails(details);
    // NG率は確認済みのデータのみで計算
    setSummary({ 
      total: totalCount, 
      ngCount: confirmedNgCount,  // 確認済みのNG数
      okCount: confirmedCount - confirmedNgCount,  // 確認済みのOK数
      confirmedCount  // 確認済みの総数
    });
  }, [isOpen, tagName, normalizedTagName, tagType, allResults]);

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen}>
      <ModalContent>
        <ModalHeader>
          <ModalTitle>
            タグ詳細
            <TagBadge>{normalizedTagName}</TagBadge>
          </ModalTitle>
          <CloseButton onClick={onClose}>&times;</CloseButton>
        </ModalHeader>

        <SummarySection>
          <SummaryItem>
            <SummaryLabel>総数</SummaryLabel>
            <SummaryValue>{summary.total}件 (確認済み: {summary.confirmedCount}件)</SummaryValue>
          </SummaryItem>
          <SummaryItem>
            <SummaryLabel>OK数</SummaryLabel>
            <SummaryValue>{summary.okCount}</SummaryValue>
          </SummaryItem>
          <SummaryItem>
            <SummaryLabel>NG数</SummaryLabel>
            <SummaryValue isNg>{summary.ngCount}</SummaryValue>
          </SummaryItem>
          <SummaryItem>
            <SummaryLabel>NG率 (確認済みのみ)</SummaryLabel>
            <SummaryValue isNg={summary.confirmedCount > 0 && summary.ngCount / summary.confirmedCount > 0.3}>
              {summary.confirmedCount > 0 ? `${((summary.ngCount / summary.confirmedCount) * 100).toFixed(1)}%` : 'データなし'}
            </SummaryValue>
          </SummaryItem>
        </SummarySection>

        {tagDetails.length > 0 ? (
          <CaseTable>
            <TableHeader>
              <tr>
                <TableHeaderCell>テストケース</TableHeaderCell>
                <TableHeaderCell>ステップ</TableHeaderCell>
                <TableHeaderCell>ステップ内容</TableHeaderCell>
                <TableHeaderCell>人間判定</TableHeaderCell>
                <TableHeaderCell>AI判定</TableHeaderCell>
                <TableHeaderCell>判定結果</TableHeaderCell>
              </tr>
            </TableHeader>
            <tbody>
              {tagDetails.map((detail, index) => (
                <TableRow 
                  key={`${detail.resultId}-${detail.stepIndex}-${index}`}
                  isClickable
                  onClick={() => onNavigateToCase(detail.resultId, detail.stepIndex - 1)}
                >
                  <TableCell>
                    <CaseIdLink>{detail.caseId}</CaseIdLink>
                    <StepInfo>{detail.caseName}</StepInfo>
                  </TableCell>
                  <TableCell>
                    <StepInfo>Step {detail.stepIndex}</StepInfo>
                  </TableCell>
                  <TableCell>
                    <StepDescription>{detail.stepDescription}</StepDescription>
                  </TableCell>
                  <TableCell>{detail.humanJudgment}</TableCell>
                  <TableCell>{detail.aiJudgment}</TableCell>
                  <TableCell>
                    <JudgmentBadge match={!detail.isNg}>
                      {detail.isNg ? 'NG' : 'OK'}
                    </JudgmentBadge>
                    {!detail.isConfirmed && (
                      <span style={{ marginLeft: '8px', color: '#ff9800', fontSize: '12px' }}>
                        (未確認)
                      </span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </tbody>
          </CaseTable>
        ) : (
          <NoDataMessage>
            このタグを含むステップが見つかりませんでした
          </NoDataMessage>
        )}
      </ModalContent>
    </Modal>
  );
};

export default TagDetailModal;